/**
 * ImportModal Component
 * Modal for importing anime data from text
 */

import { useState, useRef } from 'react'
import { Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react'
import Modal from './Modal'

export function ImportModal({ isOpen, onClose, onImport, onImportJson }) {
  const [text, setText] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [mode, setMode] = useState('raw') // 'raw' or 'json'
  const fileInputRef = useRef(null)

  const handleTextChange = (e) => {
    setText(e.target.value)
    setResult(null)
    setError(null)
  }

  const handleFileSelect = async (file) => {
    if (!file) return

    if (!file.name.endsWith('.txt') && file.type !== 'text/plain') {
      setError('テキストファイル (.txt) を選択してください')
      return
    }

    try {
      const content = await file.text()
      setText(content)
      setResult(null)
      setError(null)
    } catch (err) {
      setError('ファイルの読み込みに失敗しました')
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    setDragOver(false)

    const file = e.dataTransfer.files[0]
    handleFileSelect(file)
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    setDragOver(true)
  }

  const handleDragLeave = () => {
    setDragOver(false)
  }

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0]
    handleFileSelect(file)
  }

  const handleImport = async () => {
    if (!text.trim()) {
      setError('インポートするデータを入力してください')
      return
    }

    setLoading(true)
    setError(null)

    try {
      let importResult
      if (mode === 'json') {
        importResult = await onImportJson?.(text)
      } else {
        importResult = await onImport?.(text)
      }
      setResult(importResult)

      // 成功したら2秒後に閉じる
      setTimeout(() => {
        onClose?.()
        setText('')
        setResult(null)
        setMode('raw')
      }, 2000)
    } catch (err) {
      setError(err.message || 'インポートに失敗しました')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setText('')
    setResult(null)
    setError(null)
    onClose?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="データをインポート"
      footer={
        <>
          <button className="btn btn-secondary" onClick={handleClose}>
            キャンセル
          </button>
          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={loading || !text.trim()}
          >
            {loading ? 'インポート中...' : 'インポート'}
          </button>
        </>
      }
    >
      {/* モード切り替えタブ */}
      <div className="tabs" style={{ marginBottom: '16px' }}>
        <button
          className={`tab ${mode === 'raw' ? 'active' : ''}`}
          onClick={() => { setMode('raw'); setText(''); setError(null); setResult(null); }}
        >
          テキスト
        </button>
        <button
          className={`tab ${mode === 'json' ? 'active' : ''}`}
          onClick={() => { setMode('json'); setText(''); setError(null); setResult(null); }}
        >
          JSON
        </button>
      </div>

      {mode === 'raw' ? (
        <>
          {/* ファイルドロップエリア */}
          <div
            className={`import-area ${dragOver ? 'dragover' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={32} style={{ opacity: 0.5, marginBottom: '8px' }} />
            <p>ファイルをドロップ、またはクリックして選択</p>
            <p style={{ fontSize: '0.75rem', opacity: 0.5 }}>
              .txt ファイル対応
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,text/plain"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
          </div>

          {/* テキストエリア */}
          <div className="form-group" style={{ marginTop: '16px' }}>
            <label className="form-label">
              <FileText size={14} style={{ marginRight: '4px' }} />
              または、テキストを直接入力
            </label>
            <textarea
              className="form-input"
              value={text}
              onChange={handleTextChange}
              placeholder={`タイトル名
https://streaming-url.example.com/...
4話まで

次のタイトル
https://...
未スタート`}
              rows={10}
              style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}
            />
          </div>
        </>
      ) : (
        /* JSONモード */
        <div className="form-group">
          <label className="form-label">
            <FileText size={14} style={{ marginRight: '4px' }} />
            JSONデータを貼り付け
          </label>
          <textarea
            className="form-input"
            value={text}
            onChange={handleTextChange}
            placeholder='[{"id": "...", "title": "...", ...}]'
            rows={12}
            style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}
          />
          <p style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '8px' }}>
            「コピー」ボタンでコピーしたJSONデータを貼り付けてください
          </p>
        </div>
      )}

      {/* エラー表示 */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          marginTop: '16px',
          color: 'var(--status-dropped)'
        }}>
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* 成功表示 */}
      {result && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px',
          background: 'rgba(34, 197, 94, 0.1)',
          border: '1px solid rgba(34, 197, 94, 0.3)',
          borderRadius: '8px',
          marginTop: '16px',
          color: 'var(--status-completed)'
        }}>
          <CheckCircle size={16} />
          {result.imported}件をインポートしました
          {result.duplicates > 0 && ` (${result.duplicates}件は重複のためスキップ)`}
        </div>
      )}
    </Modal>
  )
}

export default ImportModal
